<div class="availability-management">
  <div class="header">
    <h2>
      <mat-icon>schedule</mat-icon>
      Managementul disponibilității
    </h2>
    <p class="subtitle">
      Configurați intervalele orare în care sunteți disponibil pentru programări de activități.
    </p>
  </div>

  <!-- Quick Actions -->
  <div class="quick-actions" *ngIf="!isLoading">
    <button mat-stroked-button (click)="setStandardWorkingHours()">
      <mat-icon>business_center</mat-icon>
      Program standard (Luni-Vineri 9:00-17:00)
    </button>
    
    <button mat-stroked-button color="warn" (click)="clearAllAvailability()">
      <mat-icon>clear_all</mat-icon>
      Șterge toată disponibilitatea
    </button>

    <div class="weekly-summary">
      <span class="summary-label">Total ore săptămână:</span>
      <span class="summary-value">{{ getTotalWeeklyHours() }}h</span>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-spinner diameter="50"></mat-spinner>
    <p>Se încarcă programul de disponibilitate...</p>
  </div>

  <!-- Date Range Settings -->
  <div class="date-range-section" *ngIf="!isLoading">
    <mat-card class="date-range-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>date_range</mat-icon>
          Perioada de activare a calendarului
        </mat-card-title>
        <mat-card-subtitle>
          Definiți perioada în care programul dvs. de disponibilitate este activ
        </mat-card-subtitle>
      </mat-card-header>
      
      <mat-card-content [formGroup]="dateRangeForm">
        <div class="date-range-toggle">
          <mat-slide-toggle 
            formControlName="isActive"
            (change)="onDateRangeToggle()"
            color="primary">
            <span class="toggle-text">
              {{ dateRangeForm.get('isActive')?.value ? 'Perioada activă' : 'Întotdeauna disponibil' }}
            </span>
          </mat-slide-toggle>
        </div>

        <div class="date-range-controls" *ngIf="dateRangeForm.get('isActive')?.value">
          <div class="date-fields">
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>Data de începere</mat-label>
              <input matInput [matDatepicker]="startPicker" formControlName="startDate" readonly>
              <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
              <mat-datepicker #startPicker></mat-datepicker>
              <mat-hint>Când devine activ programul</mat-hint>
            </mat-form-field>

            <div class="date-separator">
              <mat-icon>arrow_forward</mat-icon>
            </div>

            <mat-form-field appearance="outline" class="date-field">
              <mat-label>Data de încheiere</mat-label>
              <input matInput [matDatepicker]="endPicker" formControlName="endDate" readonly>
              <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
              <mat-datepicker #endPicker></mat-datepicker>
              <mat-hint>Când se dezactivează programul</mat-hint>
            </mat-form-field>
          </div>

          <div class="date-info">
            <div class="info-item">
              <mat-icon>info</mat-icon>
              <span>Programul dvs. săptămânal va fi activ doar în această perioadă</span>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Availability Form with Tabs -->
  <form [formGroup]="availabilityForm" (ngSubmit)="onSubmit()" *ngIf="!isLoading">
    
    <!-- Day Navigation Tabs -->
    <mat-tab-group [(selectedIndex)]="selectedDayIndex" class="day-tabs">
      <mat-tab *ngFor="let dayControl of getDayControls()" [label]="getDayName(dayControl.index)">
        <ng-template matTabContent>
          <div class="tab-content" [formGroupName]="dayControl.key">
            
            <!-- Day Header with Toggle -->
            <div class="day-header-card">
              <div class="day-info">
                <h2 class="day-title">
                  <mat-icon>schedule</mat-icon>
                  {{ getDayName(dayControl.index) }}
                </h2>
                <span class="day-hours" *ngIf="isDayEnabled(dayControl.index)">
                  {{ getTotalHoursForDay(dayControl.index) }}h programate
                </span>
              </div>
              
              <mat-slide-toggle 
                formControlName="enabled"
                (change)="onDayEnabledChange(dayControl.index)"
                color="primary"
                class="day-toggle">
                <span class="toggle-label">{{ isDayEnabled(dayControl.index) ? 'Disponibil' : 'Indisponibil' }}</span>
              </mat-slide-toggle>
            </div>

            <!-- Time Slots Content -->
            <div class="slots-content" *ngIf="isDayEnabled(dayControl.index); else dayDisabled">
              <div class="time-slots" formArrayName="slots">
                <div 
                  class="time-slot-card"
                  *ngFor="let slotControl of getSlotsFormArray(dayControl.index).controls; let slotIndex = index"
                  [formGroupName]="slotIndex">
                  
                  <!-- Slot Header -->
                  <div class="slot-header">
                    <div class="slot-title">
                      <span class="interval-number">Interval {{ slotIndex + 1 }}</span>
                    </div>
                    <button 
                      type="button"
                      mat-icon-button 
                      color="warn"
                      (click)="removeTimeSlot(dayControl.index, slotIndex)"
                      *ngIf="getSlotsFormArray(dayControl.index).length > 1"
                      class="delete-slot-btn"
                      [attr.aria-label]="'Șterge intervalul ' + (slotIndex + 1)">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>

                  <!-- Time Selection -->
                  <div class="slot-times">
                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>Ora început</mat-label>
                      <mat-select formControlName="start_time" required>
                        <mat-option *ngFor="let time of timeSlots" [value]="time">
                          {{ time }}
                        </mat-option>
                      </mat-select>
                      <mat-error>Selectați ora de început</mat-error>
                    </mat-form-field>

                    <div class="time-separator">
                      <mat-icon>arrow_forward</mat-icon>
                    </div>

                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>Ora sfârșit</mat-label>
                      <mat-select formControlName="end_time" required>
                        <mat-option *ngFor="let time of timeSlots" [value]="time">
                          {{ time }}
                        </mat-option>
                      </mat-select>
                      <mat-error>Selectați ora de sfârșit</mat-error>
                    </mat-form-field>
                  </div>

                  <!-- Validation Error -->
                  <div class="slot-validation" *ngIf="hasValidationErrors(dayControl.index, slotIndex)">
                    <mat-icon color="warn">warning</mat-icon>
                    <span class="error-text">{{ getValidationError(dayControl.index, slotIndex) }}</span>
                  </div>

                  <!-- Notes -->
                  <mat-form-field appearance="outline" class="notes-field">
                    <mat-label>Observații (opțional)</mat-label>
                    <input matInput formControlName="notes" placeholder="Ex: Pauza de prânz, întâlniri speciale">
                    <mat-icon matSuffix>note</mat-icon>
                  </mat-form-field>
                </div>
              </div>

              <!-- Add Interval Button -->
              <div class="add-slot-section">
                <button 
                  type="button"
                  mat-raised-button 
                  color="primary"
                  (click)="addTimeSlot(dayControl.index)"
                  class="add-slot-btn">
                  <mat-icon>add_circle</mat-icon>
                  Adaugă interval nou
                </button>
              </div>
            </div>

            <!-- Day Disabled State -->
            <ng-template #dayDisabled>
              <div class="day-disabled-card">
                <mat-icon class="disabled-icon">event_busy</mat-icon>
                <h3>Zi indisponibilă</h3>
                <p>Activați disponibilitatea pentru a configura intervalele orare pentru această zi.</p>
              </div>
            </ng-template>
            
          </div>
        </ng-template>
      </mat-tab>
    </mat-tab-group>

    <!-- Form Actions -->
    <div class="form-actions">
      <div class="form-summary">
        <div class="summary-item">
          <span class="label">Zile active:</span>
          <span class="value">{{ getActiveDaysCount() }}</span>
        </div>
        <div class="summary-item">
          <span class="label">Total ore/săptămână:</span>
          <span class="value">{{ getTotalWeeklyHours() }}h</span>
        </div>
      </div>

      <div class="action-buttons">
        <button 
          type="button" 
          mat-stroked-button 
          (click)="loadCurrentAvailability()"
          [disabled]="isSaving">
          <mat-icon>refresh</mat-icon>
          Resetează modificările
        </button>

        <button 
          type="submit" 
          mat-raised-button 
          color="primary"
          [disabled]="availabilityForm.invalid || isSaving">
          <mat-spinner diameter="20" *ngIf="isSaving" style="margin-right: 8px;"></mat-spinner>
          <mat-icon *ngIf="!isSaving">save</mat-icon>
          {{ isSaving ? 'Se salvează...' : 'Salvează programul' }}
        </button>
      </div>
    </div>
  </form>

  <!-- Help Information -->
  <mat-expansion-panel class="help-panel">
    <mat-expansion-panel-header>
      <mat-panel-title>
        <mat-icon>help</mat-icon>
        Informații despre programul de disponibilitate
      </mat-panel-title>
    </mat-expansion-panel-header>

    <div class="help-content">
      <h4>Cum funcționează:</h4>
      <ul>
        <li><strong>Intervalele de timp</strong> definesc când puteți fi programat pentru activități</li>
        <li><strong>Activitățile</strong> pot fi programate doar în intervalele definite aici</li>
        <li><strong>Conflictele</strong> sunt detectate automat când se încearcă programarea în afara orelor</li>
        <li><strong>Intervalele multiple</strong> pe zi sunt utile pentru pauze (ex: 9:00-12:00 și 13:00-17:00)</li>
      </ul>

      <h4>Sfaturi:</h4>
      <ul>
        <li>Folosiți <strong>"Program standard"</strong> pentru a seta rapid Luni-Vineri 9:00-17:00</li>
        <li>Adăugați <strong>observații</strong> pentru a specifica detalii importante</li>
        <li>Modificările se aplică <strong>imediat</strong> după salvare</li>
        <li>Programul poate fi modificat <strong>oricând</strong> fără a afecta activitățile deja programate</li>
      </ul>
    </div>
  </mat-expansion-panel>
</div>